import { scaleLinear, scalePow } from 'd3-scale';
import { max } from 'd3-array';
import {
  forceSimulation, forceX, forceY, forceCollide,
} from 'd3-force';

const MIN_R = 1.5;
const MAX_R_DESKTOP = 45;
const MAX_R_MOBILE = 28;

// Run a synchronous beeswarm simulation for one lane/column.
// anchorFn(d)  → the strong-axis target (year x on desktop, year y on mobile)
// crossAnchor  → the weak-axis target (lane center y on desktop, col center x on mobile)
// useXAnchor   → true when the strong axis is X (desktop), false when it's Y (mobile)
// halfExtent   → half-width/height of the lane for clamping
// crossExtent  → half-width/height of the column for clamping (mobile)
function buildBeeswarm(items, anchorFn, crossAnchor, useXAnchor, halfExtent) {
  if (items.length === 0) return [];

  // Give each node an initial position
  const nodes = items.map(d => ({
    ...d,
    x: useXAnchor ? anchorFn(d) : crossAnchor,
    y: useXAnchor ? crossAnchor : anchorFn(d),
    _anchor: anchorFn(d),
  }));

  const simulation = forceSimulation(nodes)
    .force(
      'anchor',
      useXAnchor
        ? forceX(d => d._anchor).strength(1.8)
        : forceY(d => d._anchor).strength(1.8),
    )
    .force(
      'cross',
      useXAnchor
        ? forceY(crossAnchor).strength(0.15)
        : forceX(crossAnchor).strength(0.15),
    )
    .force('collide', forceCollide(d => d.r + 1.5).iterations(5))
    .stop();

  for (let i = 0; i < 300; i++) simulation.tick();

  // Clamp nodes to lane bounds on the spread axis
  nodes.forEach((n) => {
    if (useXAnchor) {
      n.y = Math.max(crossAnchor - halfExtent + n.r, Math.min(crossAnchor + halfExtent - n.r, n.y));
    } else {
      n.x = Math.max(crossAnchor - halfExtent + n.r, Math.min(crossAnchor + halfExtent - n.r, n.x));
    }
  });

  return nodes;
}

export default function config({ initialWidth, isMobile }, rawData) {
  // Top margin is tall enough to hold the legend (color key + size key).
  const LEGEND_H = 90;

  const margins = isMobile
    ? {
      top: LEGEND_H + 10, right: 8, bottom: 12, left: 48,
    }
    : {
      top: LEGEND_H + 18, right: 20, bottom: 30, left: 58,
    };

  const width = initialWidth - margins.left - margins.right;

  // Parse and clean raw records
  const data = rawData.map(d => ({
    year: +d.year,
    country: d.country_clean,
    cases: Math.max(0, parseInt(d.cases_clean, 10) || 0),
    deaths: Math.max(0, parseInt(d.deaths_clean, 10) || 0),
    fatalPct: d.fatal_pct || null,
    species: d.species || '',
  }));

  const maxVal = max(data, d => Math.max(d.cases, d.deaths)) || 1;
  const maxR = isMobile ? MAX_R_MOBILE : MAX_R_DESKTOP;

  const rScale = scalePow()
    .exponent(0.4)
    .domain([0, maxVal])
    .range([0, maxR]);

  // Group outbreaks by year
  const yearGroups = {};
  data.forEach((d) => {
    if (!yearGroups[d.year]) yearGroups[d.year] = [];
    yearGroups[d.year].push(d);
  });

  let casesData;
  let deathsData;
  let initialHeight;
  let yearScale;
  let layout;

  if (!isMobile) {
    // ── Desktop layout ──────────────────────────────────────────────
    // Both lanes stacked above the year axis at the bottom.
    const LANE_HALF_H = 70;
    const LANE_GAP = 12; // gap between the two lanes

    const casesLaneCenterY = margins.top + LANE_HALF_H;
    const deathsLaneCenterY = casesLaneCenterY + LANE_HALF_H + LANE_GAP + LANE_HALF_H;
    const axisY = deathsLaneCenterY + LANE_HALF_H + 12;

    initialHeight = axisY + 25 + margins.bottom;

    yearScale = scaleLinear()
      .domain([1975, 2026])
      .range([margins.left, margins.left + width]);

    // Build beeswarm nodes per lane
    const casesNodes = [];
    const deathsNodes = [];

    Object.keys(yearGroups).forEach((yr) => {
      const year = +yr;
      yearGroups[yr].forEach((d) => {
        if (d.cases > 0) {
          casesNodes.push({ ...d, r: Math.max(MIN_R, rScale(d.cases)) });
        }
        if (d.deaths > 0) {
          deathsNodes.push({ ...d, r: Math.max(MIN_R, rScale(d.deaths)) });
        }
      });
    });

    casesData = buildBeeswarm(
      casesNodes,
      d => yearScale(d.year),
      casesLaneCenterY,
      true, // X is the strong (year) axis
      LANE_HALF_H,
    );

    deathsData = buildBeeswarm(
      deathsNodes,
      d => yearScale(d.year),
      deathsLaneCenterY,
      true,
      LANE_HALF_H,
    );

    layout = {
      casesLaneCenterY,
      deathsLaneCenterY,
      axisY,
      laneHalfH: LANE_HALF_H,
      laneGap: LANE_GAP,
      separatorY: casesLaneCenterY + LANE_HALF_H + LANE_GAP / 2,
    };
  } else {
    // ── Mobile layout ───────────────────────────────────────────────
    const YEAR_AXIS_H = 450;
    const LEGEND_EXTRA = 55;
    const ANNOTATION_COL_W = 88;
    const colWidth = (width - ANNOTATION_COL_W) / 2;
    const casesColCenterX = margins.left + colWidth / 2;
    const deathsColCenterX = margins.left + colWidth + colWidth / 2;
    const annotationColX = margins.left + colWidth * 2 + 10;

    initialHeight = margins.top + YEAR_AXIS_H + margins.bottom + LEGEND_EXTRA;

    yearScale = scaleLinear()
      .domain([1975, 2026])
      .range([margins.top, margins.top + YEAR_AXIS_H]);

    const casesNodes = [];
    const deathsNodes = [];

    Object.keys(yearGroups).forEach((yr) => {
      const year = +yr;
      yearGroups[yr].forEach((d) => {
        if (d.cases > 0) {
          casesNodes.push({ ...d, r: Math.max(MIN_R, rScale(d.cases)) });
        }
        if (d.deaths > 0) {
          deathsNodes.push({ ...d, r: Math.max(MIN_R, rScale(d.deaths)) });
        }
      });
    });

    casesData = buildBeeswarm(
      casesNodes,
      d => yearScale(d.year),
      casesColCenterX,
      false, // Y is the strong (year) axis
      colWidth / 2 - 2,
    );

    deathsData = buildBeeswarm(
      deathsNodes,
      d => yearScale(d.year),
      deathsColCenterX,
      false,
      colWidth / 2 - 2,
    );

    layout = {
      casesColCenterX,
      deathsColCenterX,
      colWidth,
      yearAxisHeight: YEAR_AXIS_H,
      annotationColX,
      annotationColW: ANNOTATION_COL_W - 10,
    };
  }

  return {
    data,
    casesData,
    deathsData,
    width,
    initialWidth,
    initialHeight,
    margins,
    rScale,
    yearScale,
    layout,
    isMobile,
    maxR,
    maxVal,
  };
}
